import {Component, Input, OnInit} from '@angular/core';
import * as XLSX from 'xlsx';
import {BehaviorSubject, Subject} from 'rxjs';
import {StepSelectFileOutput} from '../../models/step-select-file-output';
import {StepComponent, StepData} from '../../../stepper/step/step.component';
import {StepperService} from '../../../stepper/stepper.service';
import {Step} from '../../../stepper/step/step';

@Component({
    selector: 'mydocma-import-step-select-file',
    templateUrl: './step-select-file.component.html',
    styleUrls: ['./step-select-file.component.scss']
})
export class StepSelectFileComponent implements OnInit, StepComponent {

    @Input()
    public step: Step;

    public errorSubject$: BehaviorSubject<any> = new BehaviorSubject(null);

    public selectionChangeSubject = new Subject();

    public data: StepSelectFileOutput;

    public files = [];

    public workSheetNames: string[] = [];

    public selectedSheetName: string;

    private selectionTouched = false;

    constructor(
        public readonly stepperService: StepperService
    ) {
    }

    public async ngOnInit() {
        this.selectionChangeSubject.subscribe((selectedName: string) => {
            this.selectedSheetName = selectedName;
            this.selectionTouched = true;

            this.onFileChange(this.files);
        });
    }

    public enter() {
    }

    public async validate(): Promise<StepData> {
        return {
            error: this.errorSubject$.value,
            data: this.data,
            valid: true
        };
    }

    public async onFilesAdded(fileList: File[]) {
        this.files = [...this.files];
        this.files.push(...fileList);

        this.selectionTouched = false;
        await this.onFileChange(fileList);
    }

    public onFileDeleted(event) {
        if (this.files.indexOf(event)) {
            this.files.splice(event.index, 1);
            this.files = [...this.files];
        }

        this.selectionTouched = false;
        this.selectedSheetName = null;
        this.workSheetNames = [];
        this.errorSubject$.next(null);

        if (!this.files.length) {
            this.stepperService.setStepData(this.step, {
                error: null,
                data: null,
                valid: false
            });
        }
    }

    public async nextStep() {
        await this.stepperService.nextStep();
    }

    private deleteEmptyRows(sheetListData: any[][]) {
        return sheetListData.filter(((value) => {
            return !value.every((row) => row === false);
        }));
    }

    private async onFileChange(event: any) {
        if (this.errorSubject$.value) {
            this.errorSubject$.next(null);
        }

        const reader: FileReader = new FileReader();
        reader.onload = (e: any) => {
            let workSheet: XLSX.WorkSheet;
            const workBook: XLSX.WorkBook = XLSX.read(e.target.result, {type: 'binary'});

            this.workSheetNames = workBook.SheetNames;

            if (!workBook.SheetNames.length) {
                return;
            }

            if (this.workSheetNames.length > 1 && this.selectedSheetName) {
                workSheet = workBook.Sheets[this.selectedSheetName];
                this.data = {
                    data: this.deleteEmptyRows(this.xlsxToJson(workSheet)),
                    fileName: event[0].name,
                    sheetName: this.selectedSheetName,
                    columnHeaders: this.getSheetHeaders(workSheet)
                };
                this.isSheetValid(this.deleteEmptyRows(this.xlsxToJson(workSheet)));

                return;
            }

            workSheet = workBook.Sheets[workBook.SheetNames[0]];

            this.data = {
                data: this.deleteEmptyRows(this.xlsxToJson(workSheet)),
                fileName: event[0].name,
                sheetName: workBook.SheetNames[0],
                columnHeaders: this.getSheetHeaders(workSheet)
            };
            this.isSheetValid(this.deleteEmptyRows(this.xlsxToJson(workSheet)));
        };
        reader.readAsBinaryString(event[0]);
    }

    private async isSheetValid(value: any[][]) {
        if (!value || !value[0]
            || value[0].includes(undefined)
            || value[0].find((element) => typeof element !== 'string')
        ) {
            this.errorSubject$.next({sheetError: 'IE.fields.errors.wrongSheetFormat'});
            this.stepperService.setStepData(this.step, {
                error: this.errorSubject$.value,
                data: null,
                valid: false
            });

            return;
        }

        this.stepperService.setStepData(this.step, {
            error: this.errorSubject$.value,
            data: this.data,
            valid: true
        });

        if (!this.selectionTouched) {
            setTimeout(() => {
                this.stepperService.nextStep();
            });
        }
    }

    private xlsxToJson(workSheet) {
        return ((XLSX.utils.sheet_to_json(workSheet, {header: 1})) as any[][]);
    }

    private getSheetHeaders(workSheet) {
        const columnHeaders = ((XLSX.utils.sheet_to_json(workSheet, {header: 1})) as any[][])[0];
        if (!columnHeaders) {
            return;
        }

        columnHeaders.sort((a, b) => {
            if (typeof a !== 'string' || typeof b !== 'string') {
                return 0;
            }
            a.localeCompare(b);
        });

        return columnHeaders;
    }
}
